import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Database, Search, ArrowRight, Hash, Box, ArrowLeft } from 'lucide-react';

export const StorageInspector: React.FC = () => {
  const { address } = useParams<{ address: string }>();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-32 pb-20 px-8">
      <div className="max-w-7xl mx-auto">
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-[#525252] hover:text-white transition-colors mb-8 group"
        >
          <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
          Return to Trace
        </motion.button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-6">
            <Database className="w-8 h-8 text-emerald-500" />
            <h1 className="text-4xl md:text-6xl font-bold tracking-tighter text-white uppercase">Storage Inspector</h1>
          </div>
          <div className="flex items-center gap-4 p-4 bg-[#0F0F0F] border border-[#262626] rounded-xl">
            <Hash className="w-5 h-5 text-[#525252]" />
            <span className="font-mono text-xl text-white">{address || '0x...'}</span>
            <div className="ml-auto flex items-center gap-4">
              <div className="px-3 py-1 rounded bg-emerald-500/10 border border-emerald-500/20 text-[10px] font-bold uppercase tracking-widest text-emerald-500">
                VERIFIED SOURCE
              </div>
              <button className="p-2 hover:bg-white/5 rounded transition-colors text-[#525252]">
                <Search className="w-5 h-5" />
              </button>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-6">
            <section className="p-8 rounded-3xl bg-[#0F0F0F] border border-[#262626]">
              <h3 className="text-[11px] font-bold uppercase tracking-widest text-[#525252] mb-8 flex items-center gap-2">
                <Box className="w-4 h-4" />
                Storage Layout
              </h3>
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-6 p-4 rounded-xl bg-[#1A1A1A] border border-[#262626] group hover:border-emerald-500/30 transition-all">
                    <div className="w-12 text-right font-mono text-xs text-[#404040]">{i}</div>
                    <div className="flex flex-col flex-1">
                      <span className="text-xs font-bold text-white uppercase tracking-tight">mapping(address ={'>'} uint256) balances</span>
                      <span className="text-[10px] font-mono text-[#525252]">Slot: 0x{i.toString(16).padStart(64, '0')}</span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-[#404040] group-hover:text-emerald-500 transition-colors" />
                  </div>
                ))}
              </div>
            </section>
          </div>

          <div className="space-y-6">
            <section className="p-8 rounded-3xl bg-[#0F0F0F] border border-[#262626]">
              <h3 className="text-[11px] font-bold uppercase tracking-widest text-[#525252] mb-6">Contract Info</h3>
              <div className="space-y-4">
                <div className="flex flex-col gap-1">
                  <span className="text-[9px] font-bold uppercase tracking-widest text-[#404040]">Name</span>
                  <span className="text-sm font-bold text-white">Wrapped Ether (WETH)</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[9px] font-bold uppercase tracking-widest text-[#404040]">Compiler</span>
                  <span className="text-sm font-bold text-white">solc: 0.8.20</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[9px] font-bold uppercase tracking-widest text-[#404040]">Optimization</span>
                  <span className="text-sm font-bold text-white">Enabled (200 runs)</span>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};
